import { VARIABLE_PILL_CLASS } from "./variable-pill";

/** Same char stripZWS (froala content-manager) removes on save — guards are transient. */
export const PILL_CARET_GUARD = "​";

/**
 * A text sibling only doubles as a caret landing spot when its edge
 * character next to the pill is whitespace — that space is itself a
 * reachable, unambiguous non-pill position. When a pill is glued directly
 * to a word (no separating space, e.g. `‹pill›word`), the boundary between
 * the pill's own trailing text and the word's leading text is a single
 * ambiguous position the browser collapses to one side (usually *inside*
 * the pill), so there's no reachable slot for the caret to rest in — a ZWS
 * guard is required there too, same as next to an element sibling.
 */
const isWhitespace = (ch: string): boolean => /\s/.test(ch);

const edgeChar = (text: string, fromStart: boolean): string | undefined =>
  fromStart ? text[0] : text[text.length - 1];

const needsGuard = (sibling: Node | null, fromStart: boolean): boolean => {
  if (!sibling) return true;
  if (sibling.nodeType !== Node.TEXT_NODE) return true; // element (another pill, <strong>, <br>, ...) — no caret landing spot
  const text = sibling.textContent ?? "";
  const ch = edgeChar(text, fromStart);
  if (ch === undefined) return true; // empty text node — no landing spot either
  if (ch === PILL_CARET_GUARD) return false; // already guarded — idempotent
  return !isWhitespace(ch);
};

/**
 * Ensure every pill has a text-node caret landing spot on both sides.
 * contenteditable=false atoms trap the caret at block start/end and between
 * adjacent atoms; a zero-width space text node gives the browser a valid
 * caret position without visible output. Idempotent.
 */
export const ensurePillCaretGuardsInDom = (root: Element): void => {
  const pills = root.querySelectorAll(`.${VARIABLE_PILL_CLASS}`);
  const doc = root.ownerDocument;
  for (const pillEl of Array.from(pills)) {
    if (needsGuard(pillEl.previousSibling, false)) {
      pillEl.parentNode?.insertBefore(doc.createTextNode(PILL_CARET_GUARD), pillEl);
    }
    if (needsGuard(pillEl.nextSibling, true)) {
      pillEl.parentNode?.insertBefore(
        doc.createTextNode(PILL_CARET_GUARD),
        pillEl.nextSibling,
      );
    }
  }
};

export const withPillCaretGuards = (html: string): string => {
  if (!html || html.indexOf(VARIABLE_PILL_CLASS) === -1) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  ensurePillCaretGuardsInDom(doc.body);
  return doc.body.innerHTML;
};

/**
 * Find the variable pill enclosing `node`, if any. `node` may be the pill's
 * own text node (a printable key was typed while the caret sat inside the
 * pill's editable span) or the pill element itself.
 */
export const findEnclosingPill = (node: Node): HTMLElement | null => {
  const el = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  return (el?.closest(`.${VARIABLE_PILL_CLASS}`) as HTMLElement | null) ?? null;
};

/**
 * True when (node, offset) is the trailing edge of `pill` — nothing left
 * after that position anywhere inside the pill's subtree. Pills are editable
 * spans (see variable-pill.ts), so this position is a legal caret spot the
 * browser will happily park the caret in; it is visually indistinguishable
 * from being just *after* the pill, which is where typed content should go.
 */
export const isCaretAtPillEnd = (
  pill: Element,
  node: Node,
  offset: number,
): boolean => {
  if (!pill.contains(node)) return false;
  const doc = pill.ownerDocument;
  const range = doc.createRange();
  try {
    range.setStart(node, offset);
    range.setEnd(pill, pill.childNodes.length);
  } catch {
    return false;
  }
  return range.collapsed || range.toString().length === 0;
};

/**
 * Land the caret just after `pill`, reusing an existing text-node sibling or
 * creating a ZWS guard when none exists (mirrors insert-handler.ts's
 * post-insert caret placement). Returns the text node + offset to collapse a
 * Range onto.
 */
export const escapeAfterPill = (
  pill: Element,
): { node: Text; offset: number } => {
  const doc = pill.ownerDocument;
  let after = pill.nextSibling;
  if (!after || after.nodeType !== Node.TEXT_NODE) {
    after = doc.createTextNode(PILL_CARET_GUARD);
    pill.parentNode?.insertBefore(after, pill.nextSibling);
  }
  const textNode = after as Text;
  const offset = textNode.textContent?.startsWith(PILL_CARET_GUARD) ? 1 : 0;
  return { node: textNode, offset };
};

/**
 * If `sel` is a collapsed caret sitting at the trailing edge of a variable
 * pill, move it to just after the pill instead. Call this on keydown, before
 * the browser's default insert runs, so a printable character (incl. Space)
 * lands in normal text rather than extending the pill's own text node.
 * Returns true when the caret was moved.
 */
export const escapeTrailingPillCaret = (sel: Selection): boolean => {
  if (sel.rangeCount === 0 || !sel.isCollapsed) return false;
  const { focusNode, focusOffset } = sel;
  if (!focusNode) return false;

  const pill = findEnclosingPill(focusNode);
  if (!pill || !isCaretAtPillEnd(pill, focusNode, focusOffset)) return false;

  const { node, offset } = escapeAfterPill(pill);
  const doc = pill.ownerDocument;
  const range = doc.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
};

/**
 * True when `node` is the ZWS guard text node `ensurePillCaretGuardsInDom` /
 * `escapeAfterPill` place directly after a variable pill.
 */
const isPillTrailingGuard = (node: Node | null): node is Text =>
  !!node &&
  node.nodeType === Node.TEXT_NODE &&
  node.textContent === PILL_CARET_GUARD &&
  node.previousSibling !== null &&
  node.previousSibling.nodeType === Node.ELEMENT_NODE &&
  (node.previousSibling as Element).classList.contains(VARIABLE_PILL_CLASS);

/**
 * If `sel` is a collapsed caret at offset 0 of a text node that immediately
 * follows a pill's trailing ZWS guard, move it to the end of that guard
 * instead. Call this on ArrowLeft keydown, before the browser's default
 * caret movement runs: the pill↔following-text boundary is otherwise a
 * single ambiguous position, and some browsers step straight past an
 * invisible zero-width guard rather than stopping in it, landing the caret
 * inside the pill's own text instead of in the slot between pill and word.
 * Returns true when the caret was moved.
 */
export const stopBeforeFollowingText = (sel: Selection): boolean => {
  if (sel.rangeCount === 0 || !sel.isCollapsed) return false;
  const { focusNode, focusOffset } = sel;
  if (!focusNode || focusOffset !== 0) return false;
  if (focusNode.nodeType !== Node.TEXT_NODE) return false;

  const guard = focusNode.previousSibling;
  if (!isPillTrailingGuard(guard)) return false;

  const doc = guard.ownerDocument;
  const range = doc.createRange();
  range.setStart(guard, guard.length);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
};
